import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useServerFlag } from '../../hooks/useServerFlag';
import { preloadRacesRoute } from '../RouteFrame/preloadRaces';
import { Micro, SerifHeading } from '../primitives';
import styles from './NoRaceCard.module.css';

type NoRaceCardProps = {
  /** 'mobile' = full-width margin, 40px serif title.
   *  'desktop' = rail-width, 32px serif title. */
  variant?: 'mobile' | 'desktop';
  /**
   * B333 — a season is running (the macro plan resolves today to a week). The
   * coach quote then must not say "Off-season is when the next season gets
   * built" under a "Week 5 of 16" header. Default false = the off-season quote,
   * byte-identical.
   */
  seasonRunning?: boolean;
  /**
   * 2.0 Today (W1-TODAY-CARDS, TC-4). Opt-in: true draws the ratified dark
   * card on the Part 4 ramp (headline in DM Sans, phase labels with a hue dot,
   * paper "Add a race" button). Absent/false = the legacy card, byte-identical.
   */
  v2?: boolean;
  /**
   * 2.0 only. The current macro phase, derived by the route from
   * `phaseFromMacroPlan` (W1-TODAY-WIRE): it underlines that label, and
   * `'comp'` switches the headline to string 63. Non-null also means a season
   * is running (no quote). Null/absent with `seasonRunning` false = off-season.
   * Ignored unless `v2`.
   */
  phase?: NoRacePhase | null;
};

/** 2.0: the four phases the no-race and race cards print ("Comp" is competition). */
export type NoRacePhase = 'base' | 'build' | 'comp' | 'taper';

/** The quote while no season runs (the original line). */
const OFF_SEASON_QUOTE = 'Off-season is when the next season gets built.';
/** B333 — the quote while a season runs (founder ruling 2026-10-12, verbatim). */
const IN_SEASON_QUOTE = 'Pick a goal race and your plan sharpens around it.';

/** 2.0 strings 62 and 63 (Appendix B). Other phases keep 62 (recorded decision). */
const V2_HEADLINE = 'Pick a race and the plan sharpens.';
const V2_HEADLINE_COMP = "Racing season is here. Add the races you're running.";

const V2_PHASES: ReadonlyArray<{ key: NoRacePhase; label: string; dot: string }> = [
  { key: 'base', label: 'Base', dot: styles.v2DotBase },
  { key: 'build', label: 'Build', dot: styles.v2DotBuild },
  { key: 'comp', label: 'Comp', dot: styles.v2DotComp },
  { key: 'taper', label: 'Taper', dot: styles.v2DotTaper },
];

/**
 * 2.0 phase labels (string 64), shared by the no-race card and both race cards:
 * neutral text, an 8px hue dot, the current phase underlined with `--today`.
 * Hue is never the text colour. The current label is also `aria-current` so the
 * underline is not the only signal.
 */
export function PhaseLabelsV2({ current }: { current: NoRacePhase | null }) {
  return (
    <ul className={styles.v2Phases} role="list">
      {V2_PHASES.map(({ key, label, dot }) => {
        const now = key === current;
        return (
          <li
            key={key}
            className={`${styles.v2Phase} ${now ? styles.v2PhaseNow : ''}`.trim()}
            aria-current={now ? 'true' : undefined}
          >
            <span className={`${styles.v2Dot} ${dot}`} aria-hidden />
            {label}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * No-race state for the Championship card. Per Decisions & Handoff §4c:
 * runner between race seasons — plan exists, no race scheduled. Same
 * dark shell as ChampionshipCard (hiding it would collapse layout),
 * but content swaps:
 *
 *   Kicker: "No race on the calendar"
 *   Serif: "Pick a race and the plan sharpens."
 *   Phase track: muted 4-dot progression (all greyed)
 *   CTAs: "Add a race" (yellow-on-dark) / "Browse races" (ghost)
 *   Coach line: training — "Off-season is when the next season
 *              gets built." (replaces the motivational line from
 *              the with-race variant). B333: while a season runs
 *              (`seasonRunning`) the quote is "Pick a goal race and
 *              your plan sharpens around it." instead.
 *
 * **W1 update (2026-05-30) — wired the dead CTAs**: both buttons
 * were inert (no handler). "Browse races" → `/races` (the list).
 * Rendered as react-router `<Link>`s — NoRaceCard always mounts
 * inside the app Router.
 *
 * **2026-06-24 — goal-race fix**: this card is about scheduling a
 * FUTURE goal race so the plan anchors to it ("Pick a race and the
 * plan sharpens"). "Add a race" now opens the MEET form
 * (`/races?openMeetForm=1`), not the past-results race form — the
 * latter caps its date at today and can't hold a future goal race.
 */
export function NoRaceCard({
  variant = 'mobile',
  seasonRunning = false,
  v2 = false,
  phase = null,
}: NoRaceCardProps) {
  // UX defect 1 (2026-10-18): "Add a race" is the ATHLETE's door. With `races_2_0_v1` on it opens the Upcoming
  // sheet (`openRaceForm`) for every account; `openMeetForm` is the coach's door on that route, and sends a
  // dual-role account to the team-meet sheet. Above the `v2` return.
  const racesV2 = useServerFlag('races_2_0_v1');
  const addRaceTo = racesV2 ? '/races?openRaceForm=1' : '/races?openMeetForm=1';
  // W2-UX-FIX2 (defect 3): both doors below lead to Races, so with the flag on start loading its chunk now and the
  // first tap does not wait on it. A script request, never a call to the API. Above the `v2` return.
  useEffect(() => {
    if (racesV2) preloadRacesRoute();
  }, [racesV2]);
  if (v2) {
    // TC-4 (frames 1n, 1o, 1p). While a season runs (a phase is known, or the
    // route says so): headline 62 (63 in the competition phase), no quote, the
    // phase labels with the current one underlined. Off-season: headline 62,
    // the "Training coach" note with its quote, no phase labels (frame 1p).
    const inSeason = seasonRunning || phase !== null;
    return (
      <section
        className={`${styles.card} ${variant === 'desktop' ? styles.desktop : styles.mobile} ${styles.v2Card} onDark`}
      >
        <div className={styles.v2Kicker}>No race on the calendar</div>
        <div className={styles.v2Headline}>{phase === 'comp' ? V2_HEADLINE_COMP : V2_HEADLINE}</div>

        {inSeason ? (
          <PhaseLabelsV2 current={phase} />
        ) : (
          <div className={styles.v2Note}>
            <div className={styles.v2NoteKicker}>
              <span className={styles.v2NoteDot} aria-hidden />
              Training coach
            </div>
            <div className={styles.v2NoteText}>&ldquo;{OFF_SEASON_QUOTE}&rdquo;</div>
          </div>
        )}

        <div className={styles.v2Actions}>
          <Link to={addRaceTo} className={styles.v2Primary}>
            Add a race
          </Link>
          <Link to="/races" className={styles.v2Ghost}>
            Browse races
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={`${styles.card} ${variant === 'desktop' ? styles.desktop : styles.mobile}`}>
      <Micro as="div" className={styles.kicker}>
        No race on the calendar
      </Micro>
      <SerifHeading level={variant === 'desktop' ? 'md' : 'lg'} as="div" className={styles.title}>
        Pick a race and the plan sharpens.
      </SerifHeading>

      <div className={styles.dots} aria-hidden>
        <span className={styles.dot}>Base</span>
        <span className={styles.dot}>Build</span>
        <span className={styles.dot}>Comp</span>
        <span className={styles.dot}>Taper</span>
      </div>

      <div className={styles.note}>
        <div className={styles.noteKicker}>
          <span className={styles.noteDot} aria-hidden />
          Training coach
        </div>
        <div className={styles.noteText}>
          &ldquo;{seasonRunning ? IN_SEASON_QUOTE : OFF_SEASON_QUOTE}&rdquo;
        </div>
      </div>

      <div className={styles.actions}>
        <Link to={addRaceTo} className={styles.primary}>
          Add a race
        </Link>
        <Link to="/races" className={styles.ghost}>
          Browse races
        </Link>
      </div>
    </section>
  );
}
